"use client";

import type React from "react";
import { memo } from "react";

import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { TechnicianCard } from "./technician-card";
import type { ScheduledItem, ScheduleItemCalendarView } from "./lib/types";
import {
  technicianRowGridStyles,
} from "./schedular-styles";
import { ApplicationUser, SettingsLocation } from "../../models/lookupTypes";
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";

interface TechnicianRowProps {
  technician: ApplicationUser;
  days: readonly string[];
  dayLabels: { [key: string]: string };
  dates: { [key: string]: string };
  dateObjects: { [day: string]: Date };
  scheduledItems: { [day: string]: ScheduledItem[] };
  availableHours: { [day: string]: number };
  baseCapacity: { [day: string]: number };
  workOrders: any[];
  segmentParts: any[];
  locations?: SettingsLocation[]; // For location indicator
  isLocationClosed?: { [day: string]: boolean };
  isTechnicianNotWorking?: { [day: string]: boolean };
  onItemRemove: (itemId: string) => void | Promise<void>;
  onItemsRemove?: (itemIds: string[]) => void | Promise<void>;
  onItemComplete?: (itemId: string) => Promise<void>;
  readOnly?: boolean;
  scheduleItemView?: ScheduleItemCalendarView;
  onDropZone?: (day: string) => (e: React.DragEvent, date: Date) => void;
  onDropAtPosition?: (
    day: string,
  ) => (e: React.DragEvent, position: number, date: Date) => void;
  onReorderItems?: (day: string, fromItemId: string, toSortKey: number) => void;
  onUpdateItemHours?: (itemId: string, newHours: number) => Promise<void>;
  onOpenWorkOrder?: (invoiceDocId: number) => void;
  onOpenSmartWo?: (technicianId: number, date: string) => void;
}

function TechnicianRowComponent({
  technician,
  days,
  dayLabels,
  dates,
  dateObjects,
  scheduledItems,
  availableHours,
  baseCapacity,
  workOrders,
  segmentParts,
  locations = [],
  isLocationClosed = {},
  isTechnicianNotWorking = {},
  onItemRemove,
  onItemsRemove,
  onItemComplete,
  readOnly = false,
  scheduleItemView = "active",
  onDropZone,
  onDropAtPosition,
  onReorderItems,
  onUpdateItemHours,
  onOpenWorkOrder,
  onOpenSmartWo,
}: TechnicianRowProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Find technician's location name
  const technicianLocation = locations.find(
    (loc) => loc.identity === technician.defaultLocationIdentity
  );
  const locationName = technicianLocation?.displayText || "";
  // Abbreviate location name to first 3 characters for compact display
  const locationAbbr = locationName.length > 6 
    ? locationName.substring(0, 6) + "..." 
    : locationName;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Technician name header - theme-aware background, white text, compact size */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        {/* Technician name */}
        <Box
          sx={{
            display: "inline-block",
            bgcolor: isDarkMode ? "grey.800" : "primary.main", // Dark mode: grey.800, Light mode: primary blue
            color: "white",
            py: 0.75,
            px: 1,
            borderRadius: 1,
            textAlign: "center",
            minWidth: "140px", // Fixed width to accommodate 15 characters + icon + padding
            width: "140px", // Fixed width to prevent variation by name length
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: "0.85rem",
              color: "white",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <PersonIcon sx={{ fontSize: "1rem" }} />
            {technician.userName}
          </Typography>
        </Box>

        {/* Location indicator */}
        {locationName && (
          <Tooltip title={locationName} arrow>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                bgcolor: isDarkMode ? "grey.700" : "grey.200",
                color: isDarkMode ? "grey.300" : "grey.700",
                py: 0.5,
                px: 0.75,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <LocationIcon sx={{ fontSize: "0.9rem" }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {locationName}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Day cards row */}
      <Box
        sx={{
          ...technicianRowGridStyles,
          width: "100%",
          gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
        }}
      >
        {/* Day cards */}
        {days.map((day) => (
          <TechnicianCard
            // Include column date in key so week navigation remounts cards and
            // drop handlers never keep a previous week's workDate closure.
            key={`${technician.identity}-${day}-${dateObjects[day]?.getTime() ?? 0}`}
            technician={technician}
            day={day}
            date={dateObjects[day]}
            scheduledItems={scheduledItems[day] || []}
            availableHours={availableHours[day] || 0}
            baseCapacity={baseCapacity[day] || 0}
            workOrders={workOrders}
            segmentParts={segmentParts}
            isLocationClosed={isLocationClosed[day] || false}
            isTechnicianNotWorking={isTechnicianNotWorking[day] || false}
            onItemRemove={onItemRemove}
            onItemsRemove={onItemsRemove}
            onItemComplete={onItemComplete}
            readOnly={readOnly}
            scheduleItemView={scheduleItemView}
            onDropZone={
              onDropZone
                ? (e, cardDate) => onDropZone(day)(e, cardDate)
                : undefined
            }
            onDropAtPosition={
              onDropAtPosition
                ? (e, position, cardDate) =>
                    onDropAtPosition(day)(e, position, cardDate)
                : undefined
            }
            onReorderItems={(fromItemId, toSortKey) =>
              onReorderItems?.(day, fromItemId, toSortKey)
            }
            onUpdateItemHours={onUpdateItemHours}
            onOpenWorkOrder={onOpenWorkOrder}
            onOpenSmartWo={onOpenSmartWo}
          />
        ))}
      </Box>
    </Box>
  );
}

// Memoize to prevent unnecessary re-renders
const compareDayRecord = (
  prev: Record<string, number>,
  next: Record<string, number>,
  days: readonly string[],
) =>
  days.every((day) => (prev[day] ?? 0) === (next[day] ?? 0));

const compareScheduledItemsByDay = (
  prev: Record<string, ScheduledItem[]>,
  next: Record<string, ScheduledItem[]>,
  days: readonly string[],
) =>
  days.every((day) => {
    const prevItems = prev[day] ?? [];
    const nextItems = next[day] ?? [];
    if (prevItems.length !== nextItems.length) return false;
    return prevItems.every((item, index) => {
      const nextItem = nextItems[index];
      return (
        nextItem &&
        item.id === nextItem.id &&
        item.sortKey === nextItem.sortKey &&
        item.hours === nextItem.hours
      );
    });
  });

export const TechnicianRow = memo(
  TechnicianRowComponent,
  (prevProps, nextProps) =>
    prevProps.technician.identity === nextProps.technician.identity &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.scheduleItemView === nextProps.scheduleItemView &&
    prevProps.days.length === nextProps.days.length &&
    prevProps.days.every((day, index) => day === nextProps.days[index]) &&
    compareDayRecord(prevProps.availableHours, nextProps.availableHours, prevProps.days) &&
    compareDayRecord(prevProps.baseCapacity, nextProps.baseCapacity, prevProps.days) &&
    compareScheduledItemsByDay(
      prevProps.scheduledItems,
      nextProps.scheduledItems,
      prevProps.days,
    ) &&
    prevProps.days.every(
      (day) =>
        (prevProps.isLocationClosed?.[day] ?? false) ===
          (nextProps.isLocationClosed?.[day] ?? false) &&
        (prevProps.isTechnicianNotWorking?.[day] ?? false) ===
          (nextProps.isTechnicianNotWorking?.[day] ?? false),
    ) &&
    prevProps.days.every(
      (day) =>
        prevProps.dateObjects[day]?.getTime() ===
        nextProps.dateObjects[day]?.getTime(),
    ),
);
